import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useWorkspaceTabs } from "../tabs/WorkspaceTabsContext";
import { useAuth } from "../../../contexts/AuthContext";
import fetchWithAuth from "../../../utils/httpInterceptor";
import { isAvailableInDeployment } from "../../../config/deploymentMode";

/**
 * Sidebar IDAX (core-only)
 * - Mantiene el layout y estilos heredados
 * - Elimina menús NextQ/ArtonFactu que no aplican al core
 * - Carga dinámicamente los menús autogenerados desde /src/menus
 * - Soporte para redimensionamiento horizontal (solo desktop)
 */
const SidebarLeft = () => {

  const { t } = useTranslation();
  const { i18n } = useTranslation();
  const location = useLocation();
  const { openTab } = useWorkspaceTabs();
  const { hasPermission, isSuperuser, refreshMe, selectedTenant, user } = useAuth();

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [menus, setMenus] = useState([]);
  const [tenantRevision, setTenantRevision] = useState(0);
  
  // Estado para el ancho del sidebar
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    // Intentar recuperar el ancho guardado en localStorage
    const savedWidth = localStorage.getItem("sidebarWidth");
    return savedWidth ? parseInt(savedWidth, 10) : 300;
  });

  // Estado para detectar si estamos en modo desktop
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 768);

  // Refs para el manejo del resize
  const sidebarRef = useRef(null);
  const isResizing = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);
  const flowBpmPermissionsRefreshed = useRef(false);

  useEffect(() => {
    const handleTenantChanged = () => setTenantRevision((value) => value + 1);
    window.addEventListener("tenantChanged", handleTenantChanged);
    return () => window.removeEventListener("tenantChanged", handleTenantChanged);
  }, []);

  // Límites de ancho
  const MIN_WIDTH = 200;
  const MAX_WIDTH = 380;

  /**
   * Load dynamic menus generated by gen-idax-legacy
   */
  useEffect(() => {

    const resolveLocalizedText = (localized) => {
      if (!localized) return null;
      const language = (i18n.language || "en").split("-")[0];
      return localized[i18n.language] || localized[language] || localized.en || localized.es || null;
    };

    const loadMenus = async () => {

      const context = require.context("../../../menus", true, /\.menu\.js$/);

      const loaded = context.keys().map((key) => context(key).default);

      const remoteMenus = [];
      setMenus(loaded);
      try {const response=await fetchWithAuth("/api/ledger/module/status");const status=await response.json();if(status?.available){const manifestResponse=await fetch("/ledger/generated/ledger/manifest.generated.json",{cache:"no-store"});if(manifestResponse.ok){const manifest=await manifestResponse.json();remoteMenus.push(...(manifest?.menus||[]).map(menu=>({...menu,labelText:resolveLocalizedText(menu.labelText),groupLabelText:resolveLocalizedText(menu.groupLabelText)})));}}}catch(error){}
      try {
        const response = await fetchWithAuth("/api/realestate/module/status");
        const status = await response.json();
        if (status?.available) {
          const manifestResponse = await fetch("/realestate/generated/realestate/manifest.generated.json", { cache: "no-store" });
          if (manifestResponse.ok) {
            const manifest = await manifestResponse.json();
            remoteMenus.push(...(manifest?.menus || []).map((menu) => ({...menu,labelText:resolveLocalizedText(menu.labelText),groupLabelText:resolveLocalizedText(menu.groupLabelText)})));
          }
        }
      } catch (error) {
        // Optional RealEstate module: no menu when unavailable.
      }
      setMenus([...loaded, ...remoteMenus]);
      try {
        const hrStatusResponse = await fetchWithAuth("/api/hr/module/status");
        const hrStatus = await hrStatusResponse.json();
        if (hrStatus?.available) {
          const hrManifestResponse = await fetch("/hr/generated/hr/manifest.generated.json", { cache: "no-store" });
          if (hrManifestResponse.ok) {
            const hrManifest = await hrManifestResponse.json();
            remoteMenus.push(...(hrManifest?.menus || []).map((menu) => ({
              ...menu,
              labelText: resolveLocalizedText(menu.labelText),
              groupLabelText: resolveLocalizedText(menu.groupLabelText),
            })));
          }
        }
      } catch (error) {
        // Optional HR module: no menu when unavailable.
      }
      setMenus([...loaded, ...remoteMenus]);

      try {
        const statusResponse = await fetchWithAuth("/api/factuflow/module/status");
        const status = await statusResponse.json();
        if (status?.available) {
          const manifestResponse = await fetch("/factuflow/generated/factuflow/manifest.generated.json", {
            cache: "no-store",
          });
          if (manifestResponse.ok) {
            const manifest = await manifestResponse.json();
            remoteMenus.push(...(manifest?.menus || []).map((menu) => ({
              ...menu,
              labelText: resolveLocalizedText(menu.labelText),
              groupLabelText: resolveLocalizedText(menu.groupLabelText),
            })));
          }
        }
      } catch (error) {
        // Optional FactuFlow module: preserve menus already loaded from other modules.
      }
      try {
        const flowStatusResponse = await fetchWithAuth("/api/flow-bpm/module/status");
        const flowStatus = await flowStatusResponse.json();
        const canAccessFlowBpm = isSuperuser || user?.bpmUserEnabled === true;
        if (flowStatus?.available && canAccessFlowBpm) {
          if (!flowBpmPermissionsRefreshed.current && !hasPermission("flow-bpm.task.read")) {
            flowBpmPermissionsRefreshed.current = true;
            await refreshMe().catch(() => null);
          }
          remoteMenus.push({
            key: "flowBpmTasks",
            route: "/flow-bpm",
            label: "sidebar.flowBpm",
            permission: "flow-bpm.task.read",
            icon: "faDiagramProject",
            group: "bpm",
            order: 18,
          });
          remoteMenus.push({
            key: "flowBpmUi",
            route: "/flow-bpm-ui",
            label: "sidebar.flowBpmUi",
            permission: "flow-bpm.task.read",
            icon: "faWindowMaximize",
            group: "bpm",
            order: 19,
          });
          remoteMenus.push({
            key: "flowBpmModeler",
            route: "/flow-bpm-modeler",
            label: "sidebar.flowBpmModeler",
            permission: "flow-bpm.modeler.access",
            icon: "faPencilRuler",
            group: "bpm",
            order: 20,
          });
        }
      } catch (error) {
        // Optional module: no menu when the API is not reachable.
      }

      setMenus([...loaded, ...remoteMenus]);

    };

    loadMenus();

  }, [hasPermission, i18n.language, isSuperuser, refreshMe, selectedTenant?.id, selectedTenant?.tenantId, tenantRevision, user?.bpmUserEnabled]);

  const canManageUsers = hasPermission("system.users.read");

  /**
   * Listener para actualizar isDesktop cuando cambia el viewport
   */
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidebar = () => {
    setIsCollapsed((v) => !v);

    if (window.innerWidth >= 768) {
      document.documentElement.classList.toggle("sidebar-left-collapsed");
    }
  };

  const handleLinkClick = () => {
    if (window.innerWidth < 768) {
      setIsCollapsed(false);
      document.documentElement.classList.remove("sidebar-left-opened");
    }
  };

  const handleRouteOpen = (path, label, icon, fallbackLabel) => {
    openTab(path, label ? renderLabel(label, fallbackLabel) : fallbackLabel, icon);
    handleLinkClick();
  };

  useEffect(() => {

    if (isCollapsed) {
      document.documentElement.classList.add("sidebar-left-collapsed");
    } else {
      document.documentElement.classList.remove("sidebar-left-collapsed");
    }

    return () => {
      document.documentElement.classList.remove("sidebar-left-collapsed");
    };

  }, [isCollapsed]);

  const isActive = (path) => location.pathname === path;

  const toggleMenu = (menuKey) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [menuKey]: !prev[menuKey],
    }));
  };

  const isMenuExpanded = (menuKey) => expandedMenus[menuKey] === true;

  /**
   * Group dynamic menus by sidebar group
   */
  const menusByGroup = menus.filter((menu) => isAvailableInDeployment(menu) && (isSuperuser || hasPermission(menu.permission))).reduce((acc, menu) => {

    const group = menu.group || "misc";

    if (!acc[group]) acc[group] = [];

    acc[group].push(menu);

    return acc;

  }, {});

  /**
   * Metadata for sidebar groups
   */
  const GROUP_META = {
    inventory: { icon: "fa fa-boxes", label: "sidebar.inventory" },
    sales: { icon: "fa fa-shopping-cart", label: "sidebar.sales" },
    purchases: { icon: "fa fa-truck", label: "sidebar.purchases" },
    accounting: { icon: "fa fa-calculator", label: "sidebar.accounting" },
    finance: { icon: "fa fa-line-chart", label: "sidebar.finance" },
    hr: { icon: "fa fa-users", label: "hr.title" },
    realestate: { icon: "fa fa-building", label: "realestate.title" },
    ledger: { icon: "fa fa-link", label: "ledger.title" },
    bpm: { icon: "fa fa-diagram-project", label: "sidebar.bpm" },
    documentation: { icon: "fa fa-book", label: "sidebar.documentation" },
    configuration: { icon: "fa fa-sliders", label: "sidebar.configuration" },
    masterdata: { icon: "fa fa-database", label: "sidebar.masterData" },
    cms: { icon: "fa fa-newspaper", label: "sidebar.cms" },
    visits: { icon: "fa fa-calendar", label: "sidebar.visits" },
    aiDevelopment: { icon: "fa fa-code", label: "aiDevelopment.title" }
  };

  const getGroupMeta = (groupKey) => {
    if (GROUP_META[groupKey]) {
      const firstMenu = menusByGroup[groupKey]?.[0];
      return {
        ...GROUP_META[groupKey],
        labelText: firstMenu?.groupLabelText,
      };
    }
    const firstMenu = menusByGroup[groupKey]?.[0];
    if (!firstMenu) {
      return null;
    }
    return {
      icon: firstMenu.groupIcon ? `fa ${convertIconName(firstMenu.groupIcon)}` : "fa fa-folder",
      label: firstMenu.groupLabel,
      labelText: firstMenu.groupLabelText,
    };
  };

  const renderLabel = (key, fallback) => {
    const translated = key ? t(key) : "";
    return translated && translated !== key ? translated : fallback || translated || key;
  };

  /**
   * Convierte nombre de icono de camelCase a kebab-case
   * Ejemplo: faUsers -> fa-users, faBox -> fa-box
   */
  const convertIconName = (iconName) => {
    if (!iconName) return null;
    // Convertir camelCase a kebab-case
    return iconName.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
  };

  /**
   * Render menu items of a group
   */
  const renderMenuItems = (group, options = {}) => {

    const items = (menusByGroup[group] || [])
      .sort((a, b) => (a.order || 0) - (b.order || 0));

    if (!items.length) {
      if (options.hideEmpty) {
        return null;
      }
      return (
        <li className="nav-disabled">
          <span>
            <i className="fa fa-clock-o" aria-hidden="true"></i>{" "}
            <span className="text-muted">{t("sidebar.comingSoon")}</span>
          </span>
        </li>
      );
    }

    return items.map((menu) => (
      <li key={menu.key || menu.route} className={isActive(menu.route) ? "nav-active" : ""}>
        <Link to={menu.route} onClick={() => handleRouteOpen(menu.route, menu.label, menu.icon, menu.labelText)}>

          {menu.icon && (
            <i className={`fa ${convertIconName(menu.icon)}`} aria-hidden="true"></i>
          )}

          <span>{renderLabel(menu.label, menu.labelText)}</span>

        </Link>
      </li>
    ));
  };

  /**
   * Render dynamic sidebar group
   */
  const renderDynamicGroup = (groupKey) => {

    const meta = getGroupMeta(groupKey);
    if (!meta) return null;
    if (!(menusByGroup[groupKey] || []).length) return null;

    return (
      <li className={`nav-parent ${isMenuExpanded(groupKey) ? "nav-expanded" : ""}`}>

        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <a
          className="nav-toggle"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            toggleMenu(groupKey);
          }}
        >
          <i className={meta.icon} aria-hidden="true"></i>
          <span>{renderLabel(meta.label, meta.labelText)}</span>
        </a>

        <ul className="nav nav-children">
          {renderMenuItems(groupKey)}
        </ul>

      </li>
    );
  };

  // Conditionally render Tenants menu only for superusers
  const tenantsMenu = isSuperuser ? (
    <li className={isActive("/tenants") ? "nav-active" : ""}>
      <Link to="/tenants" onClick={() => handleRouteOpen("/tenants", "sidebar.tenants")}>
        <i className="fa fa-sitemap" aria-hidden="true"></i>
        <span>{t("sidebar.tenants")}</span>
      </Link>
    </li>
  ) : null;

  /**
   * Limpiar estilos inline del sidebar y layout
   * Se usa cuando estamos en móvil o cuando el sidebar está colapsado
   */
  const clearInlineStyles = useCallback(() => {
    if (sidebarRef.current) {
      sidebarRef.current.style.width = '';
    }
    
    const contentBody = document.querySelector('.content-body');
    if (contentBody) {
      contentBody.style.marginLeft = '';
    }
    
    const pageHeader = document.querySelector('.page-header');
    if (pageHeader) {
      pageHeader.style.left = '';
    }
  }, []);

  /**
   * Aplicar estilos inline del sidebar y layout
   * Solo se debe llamar cuando estamos en desktop y el sidebar no está colapsado
   */
  const applyInlineStyles = useCallback((width) => {
    if (sidebarRef.current) {
      sidebarRef.current.style.width = `${width}px`;
    }

    const contentMargin = width + 20;
    const contentBody = document.querySelector('.content-body');
    if (contentBody) {
      contentBody.style.marginLeft = `${contentMargin}px`;
    }

    const pageHeader = document.querySelector('.page-header');
    if (pageHeader) {
      pageHeader.style.left = `${width}px`;
    }
  }, []);

  /**
   * Actualizar el ancho del sidebar y el margen del contenido principal
   * Solo aplica en desktop
   */
  const updateSidebarWidth = useCallback((newWidth) => {
    // Solo aplicar si estamos en desktop
    if (!isDesktop) return;
    
    // Aplicar límites
    const clampedWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, newWidth));
    
    // Actualizar estado
    setSidebarWidth(clampedWidth);
    
    // Guardar en localStorage
    localStorage.setItem("sidebarWidth", clampedWidth.toString());
    
    // Aplicar estilos inline
    applyInlineStyles(clampedWidth);
  }, [isDesktop, applyInlineStyles]);

  /**
   * Manejadores de eventos para el resize
   */
  const handleMouseMove = useCallback((e) => {
    if (!isResizing.current) return;
    
    const deltaX = e.clientX - startX.current;
    const newWidth = startWidth.current + deltaX;
    
    updateSidebarWidth(newWidth);
  }, [updateSidebarWidth]);

  const handleMouseUp = useCallback(() => {
    isResizing.current = false;
    
    // Remover clase del body
    document.body.classList.remove('sidebar-resizing');
    
    // Remover listeners globales
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseMove]);

  const handleMouseDown = useCallback((e) => {
    // Solo permitir resize si el sidebar no está colapsado Y estamos en desktop
    if (isCollapsed || !isDesktop) return;
    
    e.preventDefault();
    isResizing.current = true;
    startX.current = e.clientX;
    startWidth.current = sidebarWidth;
    
    // Añadir clase al body para prevenir selección de texto
    document.body.classList.add('sidebar-resizing');
    
    // Añadir listeners globales
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [isCollapsed, isDesktop, sidebarWidth, handleMouseMove, handleMouseUp]);

  /**
   * Efecto principal: aplicar o limpiar estilos inline según breakpoint y estado
   * - Desktop + expandido: aplicar ancho guardado
   * - Desktop + colapsado: limpiar estilos
   * - Móvil: siempre limpiar estilos (el CSS responsive se encarga)
   */
  useEffect(() => {
    if (isDesktop && !isCollapsed) {
      // Desktop y expandido: aplicar estilos inline
      applyInlineStyles(sidebarWidth);
    } else {
      // Móvil o colapsado: limpiar estilos inline
      clearInlineStyles();
    }
  }, [isDesktop, isCollapsed, sidebarWidth, applyInlineStyles, clearInlineStyles]);

  /**
   * Cleanup de event listeners al desmontar
   */
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  return (
    <aside
      ref={sidebarRef}
      id="sidebar-left"
      className={`sidebar-left ${isCollapsed ? "collapsed" : ""}`}
    >

      <div className="sidebar-header">

        <div className="sidebar-title">{t("sidebar.navigation")}</div>

        <div className="sidebar-toggle" onClick={toggleSidebar}>
          <i className="fa fa-bars" aria-label="Toggle sidebar"></i>
        </div>

      </div>

      <div className="nano">
        <div className="nano-content">

          <nav id="menu" className="nav-main" role="navigation">

            <ul className="nav nav-main">

              {/* Dashboard */}
              <li className={isActive("/dashboard") ? "nav-active" : ""}>
                <Link to="/dashboard" onClick={() => handleRouteOpen("/dashboard", "sidebar.dashboard")}>
                  <i className="fa fa-home" aria-hidden="true"></i>
                  <span>{t("sidebar.dashboard")}</span>
                </Link>
              </li>

              {renderDynamicGroup("documentation")}

              {/* Sistema */}
              <li className={`nav-parent ${isMenuExpanded("system") ? "nav-expanded" : ""}`}>

                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <a
                  className="nav-toggle"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    toggleMenu("system");
                  }}
                >
                  <i className="fa fa-cogs" aria-hidden="true"></i>
                  <span>{t("sidebar.system")}</span>
                </a>

                <ul className="nav nav-children">

                  <li className={isActive("/profile") ? "nav-active" : ""}>
                    <Link to="/profile" onClick={() => handleRouteOpen("/profile", "sidebar.profile")}>
                      <i className="fa fa-id-card" aria-hidden="true"></i>
                      <span>{t("sidebar.profile")}</span>
                    </Link>
                  </li>

                  {tenantsMenu}

                  {canManageUsers && (
                    <li className={isActive("/tenant-users") ? "nav-active" : ""}>
                      <Link to="/tenant-users" onClick={() => handleRouteOpen("/tenant-users", isSuperuser ? "sidebar.tenantUsers" : "sidebar.users")}>
                        <i className="fa fa-users" aria-hidden="true"></i>
                        <span>{isSuperuser ? t("sidebar.tenantUsers") : t("sidebar.users")}</span>
                      </Link>
                    </li>
                  )}

                  {hasPermission("system.roles.read") && (
                    <li className={isActive("/roles") ? "nav-active" : ""}>
                      <Link to="/roles" onClick={() => handleRouteOpen("/roles", "sidebar.roles")}>
                        <i className="fa fa-user-shield" aria-hidden="true"></i>
                        <span>{t("sidebar.roles")}</span>
                      </Link>
                    </li>
                  )}

                  {hasPermission("messaging.read") && (
                    <li className={isActive("/messages") ? "nav-active" : ""}>
                      <Link to="/messages" onClick={() => handleRouteOpen("/messages", "sidebar.messages")}>
                        <i className="fa fa-envelope" aria-hidden="true"></i>
                        <span>{t("sidebar.messages")}</span>
                      </Link>
                    </li>
                  )}

                  {hasPermission("system.dataareas.read") && (
                    <li className={isActive("/tenant-dataareas") ? "nav-active" : ""}>
                      <Link to="/tenant-dataareas" onClick={() => handleRouteOpen("/tenant-dataareas", "sidebar.dataAreas")}>
                        <i className="fa fa-database" aria-hidden="true"></i>
                        <span>{t("sidebar.dataAreas")}</span>
                      </Link>
                    </li>
                  )}

                  {/* Dynamic system menus */}
                  {renderMenuItems("system", { hideEmpty: true })}

                </ul>

              </li>

              {renderDynamicGroup("inventory")}
              {renderDynamicGroup("sales")}
              {renderDynamicGroup("purchases")}
              {renderDynamicGroup("accounting")}
              {renderDynamicGroup("finance")}
              {renderDynamicGroup("hr")}
              {renderDynamicGroup("realestate")}
              {renderDynamicGroup("ledger")}
              {renderDynamicGroup("bpm")}
              {renderDynamicGroup("configuration")}
              {renderDynamicGroup("masterdata")}
              {renderDynamicGroup("cms")}
              {renderDynamicGroup("visits")}
              {renderDynamicGroup("aiDevelopment")}
              {Object.keys(menusByGroup)
                .filter((groupKey) => !GROUP_META[groupKey] && groupKey !== "system")
                .sort((a, b) => {
                  const left = menusByGroup[a]?.[0]?.groupOrder || 100;
                  const right = menusByGroup[b]?.[0]?.groupOrder || 100;
                  return left - right;
                })
                .map((groupKey) => renderDynamicGroup(groupKey))}

            </ul>

          </nav>

        </div>
      </div>

      {/* Resize handle - solo visible cuando el sidebar no está colapsado Y estamos en desktop */}
      {!isCollapsed && isDesktop && (
        <div
          className="sidebar-resize-handle"
          onMouseDown={handleMouseDown}
          title={t("sidebar.resizeHint") || "Arrastrar para redimensionar"}
        />
      )}

    </aside>
  );
};

export default SidebarLeft;
